import React, { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const SUBJECTS = [
  "Sugestão de projeto",
  "Pedido / demanda do bairro",
  "Convite para evento",
  "Imprensa",
  "Outro assunto",
];

export default function ParticipateForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    neighborhood: "",
    subject: SUBJECTS[0],
    message: "",
  });
  const [status, setStatus] = useState({ loading: false, ok: false, err: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, err: "" });
    try {
      await api.post("/messages", form);
      setStatus({ loading: false, ok: true, err: "" });
      setForm({ name: "", email: "", phone: "", neighborhood: "", subject: SUBJECTS[0], message: "" });
    } catch (err) {
      setStatus({ loading: false, ok: false, err: formatApiError(err) });
    }
  };

  return (
    <section
      id="participe"
      data-testid="participate-section"
      className="py-24 md:py-32 bg-[#003F88] text-white border-t border-[#003F88]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#FFC72C] font-semibold">
            <span className="inline-block w-14 h-[3px] bg-[#FFC72C] align-middle mr-4" />
            Participação Popular
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight mt-6">
            Eu quero <em className="italic font-light">ouvir você</em>.
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-white/80 font-light max-w-md">
            Lauro de Freitas se constrói com escuta. Conte sobre o seu bairro, envie uma sugestão de
            projeto ou simplesmente diga oi. Toda mensagem é lida pela nossa equipe.
          </p>
          <div className="mt-10 space-y-4 text-sm text-white/80">
            <div><span className="text-[#FFC72C] uppercase tracking-widest text-xs">WhatsApp · </span>(71) 99231-0448</div>
            <div><span className="text-[#FFC72C] uppercase tracking-widest text-xs">E-mail · </span>contato@antoniorosalvo.com.br</div>
            <div><span className="text-[#FFC72C] uppercase tracking-widest text-xs">Instagram · </span>@rosalvo_oficial</div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="participate-form"
          className="lg:col-span-7 bg-white text-neutral-900 p-8 md:p-12 space-y-5"
        >
          {status.ok && (
            <div
              data-testid="participate-success"
              className="flex items-start gap-3 p-4 bg-[#FFC72C]/30 border-l-4 border-[#FFC72C]"
            >
              <CheckCircle2 className="text-[#003F88] shrink-0" size={20} />
              <div className="text-sm">
                <strong>Mensagem enviada!</strong> A equipe do Rosalvo recebeu sua mensagem e entrará em contato em breve.
              </div>
            </div>
          )}
          {status.err && (
            <div data-testid="participate-error" className="p-4 bg-red-50 border-l-4 border-red-500 text-sm text-red-800">
              {status.err}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Nome completo" required>
              <input
                data-testid="form-name"
                required
                value={form.name}
                onChange={update("name")}
                className="w-full bg-transparent border-b border-neutral-300 py-3 focus:border-[#003F88] outline-none text-base"
              />
            </Field>
            <Field label="E-mail" required>
              <input
                data-testid="form-email"
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                className="w-full bg-transparent border-b border-neutral-300 py-3 focus:border-[#003F88] outline-none text-base"
              />
            </Field>
            <Field label="WhatsApp / Telefone">
              <input
                data-testid="form-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="(71) 9...."
                className="w-full bg-transparent border-b border-neutral-300 py-3 focus:border-[#003F88] outline-none text-base"
              />
            </Field>
            <Field label="Bairro">
              <input
                data-testid="form-neighborhood"
                value={form.neighborhood}
                onChange={update("neighborhood")}
                placeholder="Jambeiro, Itinga, Vilas..."
                className="w-full bg-transparent border-b border-neutral-300 py-3 focus:border-[#003F88] outline-none text-base"
              />
            </Field>
          </div>
          <Field label="Assunto" required>
            <select
              data-testid="form-subject"
              value={form.subject}
              onChange={update("subject")}
              className="w-full bg-transparent border-b border-neutral-300 py-3 focus:border-[#003F88] outline-none text-base"
            >
              {SUBJECTS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Mensagem" required>
            <textarea
              data-testid="form-message"
              required
              rows={5}
              value={form.message}
              onChange={update("message")}
              className="w-full bg-transparent border border-neutral-300 px-4 py-3 focus:border-[#003F88] outline-none text-base resize-none"
            />
          </Field>
          <button
            type="submit"
            data-testid="form-submit"
            disabled={status.loading}
            className="inline-flex items-center gap-3 bg-[#003F88] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#002A5C] transition-colors disabled:opacity-60"
          >
            {status.loading ? "Enviando..." : "Enviar mensagem"} <Send size={14} />
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.22em] text-neutral-500 font-medium">
        {label}{required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}
